import type { OptimizelyDecision } from '@optimizely/optimizely-sdk';
import { Experiment } from 'lib/sdks/optimizely';

export const ALGORITHM_STORAGE_KEY = 'algorithm';

function isValidAlgorithm(maybeAlgo: string | unknown) {
  return typeof maybeAlgo === 'string' && maybeAlgo.length > 0;
}

const algorithms = {
  sort_by_review_score: 'sort_by_reviews_score',
} as const;

function persistAlgo(algo: string) {
  const PPC_ALGORITHM_PREFIX = 'ppc';

  if (algo.startsWith(PPC_ALGORITHM_PREFIX)) {
    return true;
  }

  if (algorithms[algo as keyof typeof algorithms]) {
    return true;
  }

  return false;
}

const isExperimentAlgoEnabled = (
  decision?: OptimizelyDecision,
): decision is OptimizelyDecision =>
  !!decision &&
  decision.enabled &&
  decision.variationKey === 'variant' &&
  typeof decision.variables?.algorithm_name === 'string';

export function getAlgorithm(
  query: { algorithm?: string | undefined },
  decide: (experimentKey: Experiment) => OptimizelyDecision | undefined,
): string | undefined {
  const sessionStorageAvailable = typeof sessionStorage !== 'undefined';
  let algorithm = undefined;

  switch (true) {
    case isValidAlgorithm(query?.algorithm):
      algorithm = query.algorithm as string;

      if (persistAlgo(algorithm)) {
        if (sessionStorageAvailable) {
          sessionStorage.setItem(ALGORITHM_STORAGE_KEY, algorithm);
        }
      } else {
        if (sessionStorageAvailable) {
          sessionStorage.removeItem(ALGORITHM_STORAGE_KEY);
        }

        if (decide) {
          decide(Experiment.SEARCH_ALGO_EXPERIMENT);
        }
      }

      break;
    case sessionStorageAvailable &&
      isValidAlgorithm(sessionStorage.getItem(ALGORITHM_STORAGE_KEY)):
      algorithm = sessionStorage.getItem(ALGORITHM_STORAGE_KEY) as string;
      break;
    case typeof decide === 'function':
      const decision = decide(Experiment.SEARCH_ALGO_EXPERIMENT);

      if (isExperimentAlgoEnabled(decision)) {
        algorithm = decision.variables?.algorithm_name as string;
      }
  }

  return algorithm;
}
